"use client";
import { useHeaderHeight } from "@/app/providers/navigationProvider";
import { getCloverLinks } from "@/utils/cloverLinks";
import { PRIVACY_POLICY_URL } from "@/utils/constants";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import { LocalStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { DateTime } from "luxon";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

export const CookiesBanner = () => {
  const t = useTranslations("CookiesBanner");
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const { recalculateHeight } = useHeaderHeight();
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const locale = useLocale();

  useEffect(() => {
    setIsPreview(window.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    const bannerSeenTimestamp = getFromLocalStorage(
      LocalStorageKey.COOKIE_BANNER_SEEN,
      ""
    );
    if (bannerSeenTimestamp !== "") {
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  const handleBannerDismiss = () => {
    saveToLocalStorage(
      LocalStorageKey.COOKIE_BANNER_SEEN,
      DateTime.now().toFormat("dd-MMM-yyyy h:m a")
    );
    setShowBanner(false);
    setTimeout(() => {
      recalculateHeight();
    });
  };

  const privacyLink = useMemo(() => {
    return getCloverLinks(locale)?.privacy ?? PRIVACY_POLICY_URL;
  }, [locale]);

  const renderCookieMessage = (chunks: React.ReactNode) => (
    <a
      href={privacyLink}
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-[#3271AB]"
      aria-label={t("privacyPolicyAriaLabel")}
    >
      {chunks}
    </a>
  );

  if (showBanner && !isPreview)
    return (
      <div className="w-full h-[148px] md:h-[77px] bg-[#FFF6CC] p-3 flex flex-col sm:flex-row justify-center items-center sm:px-16 border-[#FFD100] border">
        <div className="flex flex-col sm:flex-row items-center w-full max-w-[1450px] gap-2 sm:gap-0">
          <p className="text-[15px] ">
            {t.rich("message", {
              a: (chunks) => renderCookieMessage(chunks),
            })}
          </p>
          <button
            data-testid={applyDataTestId(pom.header.dismissCookieBanner)}
            onClick={handleBannerDismiss}
            className="w-full rounded-md bg-[#727272] hover:bg-[#616161] text-white flex flex-row items-center justify-center text-[16px] font-medium h-[40px] cursor-pointer"
            aria-label={t("dismiss")}
          >
            {t("dismiss")}
          </button>
        </div>
      </div>
    );

  return null;
};
